import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import useEscapeToClose from '../../utilities/useEscapeToClose.js';

const FRIENDLY_ELEMENT_NAMES = {
  a: 'Link',
  article: 'Section',
  blockquote: 'Quote',
  figcaption: 'Caption',
  figure: 'Image section',
  footer: 'Footer',
  h1: 'Main heading',
  h2: 'Section heading',
  h3: 'Subheading',
  h4: 'Heading',
  h5: 'Heading',
  h6: 'Heading',
  header: 'Introduction',
  img: 'Image',
  li: 'List item',
  main: 'Main content',
  ol: 'Numbered list',
  p: 'Paragraph',
  projects: 'Projects',
  section: 'Section',
  skills: 'Skills',
  table: 'Table',
  ul: 'List',
};

const actionShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  iconClass: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  danger: PropTypes.bool,
});

const MobileElementActionBar = ({
  elementType,
  editAction,
  aiAction,
  moveActions,
  moreActions,
  onOpenRearrange,
}) => {
  const [openPanel, setOpenPanel] = useState('');
  const [navigatorSlot, setNavigatorSlot] = useState(null);
  useEscapeToClose(Boolean(openPanel), () => setOpenPanel(''));

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    setNavigatorSlot(document.querySelector('[data-mobile-element-actions-slot]'));
    return undefined;
  }, []);

  if (typeof document === 'undefined' || !editAction || !navigatorSlot) return null;

  const runAction = (action) => {
    if (!action || action.disabled) return;
    setOpenPanel('');
    action.onClick();
  };
  const togglePanel = (panel) => {
    setOpenPanel((current) => current === panel ? '' : panel);
  };
  const renderPanelAction = (action) => (
    <button
      className={`btn btn-sm mobile-element-action-panel-button ${
        action.danger ? 'btn-outline-danger' : 'btn-light'
      }`}
      type="button"
      disabled={action.disabled}
      onClick={() => runAction(action)}
      key={action.key}
    >
      <i className={`bi ${action.iconClass}`} aria-hidden="true"></i>
      <span>{action.title}</span>
    </button>
  );

  return createPortal(
    <div
      className="mobile-element-action-bar"
      aria-label={`Actions for ${FRIENDLY_ELEMENT_NAMES[elementType] || 'selected content'}`}
      onClick={(event) => event.stopPropagation()}
    >
      {openPanel ? (
        <div className="mobile-element-action-panel" aria-label={`${openPanel} actions`}>
          <div className="mobile-element-action-panel-title">
            {openPanel === 'move' ? 'Move content' : 'More actions'}
          </div>
          <div className="mobile-element-action-panel-grid">
            {(openPanel === 'move' ? moveActions : moreActions).map(renderPanelAction)}
          </div>
        </div>
      ) : null}

      <div className="mobile-element-action-primary" role="toolbar" aria-label="Selected content actions">
        <button className="btn btn-sm btn-light" type="button" onClick={() => runAction(editAction)}>
          <i className={`bi ${editAction.iconClass}`} aria-hidden="true"></i>
          <span>Edit</span>
        </button>
        {aiAction ? (
          <button className="btn btn-sm btn-light" type="button" onClick={() => runAction(aiAction)}>
            <i className={`bi ${aiAction.iconClass}`} aria-hidden="true"></i>
            <span>AI</span>
          </button>
        ) : null}
        {onOpenRearrange || moveActions.length ? (
          <button
            className={`btn btn-sm ${openPanel === 'move' ? 'btn-primary' : 'btn-light'}`}
            type="button"
            onClick={() => {
              if (onOpenRearrange) onOpenRearrange();
              else togglePanel('move');
            }}
            aria-expanded={onOpenRearrange ? undefined : openPanel === 'move'}
          >
            <i className="bi bi-arrows-move" aria-hidden="true"></i>
            <span>Move</span>
          </button>
        ) : null}
        {moreActions.length ? (
          <button
            className={`btn btn-sm ${openPanel === 'more' ? 'btn-primary' : 'btn-light'}`}
            type="button"
            onClick={() => togglePanel('more')}
            aria-expanded={openPanel === 'more'}
          >
            <i className="bi bi-three-dots" aria-hidden="true"></i>
            <span>More</span>
          </button>
        ) : null}
      </div>
    </div>,
    navigatorSlot
  );
};

MobileElementActionBar.propTypes = {
  elementType: PropTypes.string.isRequired,
  editAction: actionShape,
  aiAction: actionShape,
  moveActions: PropTypes.arrayOf(actionShape),
  moreActions: PropTypes.arrayOf(actionShape),
  onOpenRearrange: PropTypes.func,
};

MobileElementActionBar.defaultProps = {
  editAction: null,
  aiAction: null,
  moveActions: [],
  moreActions: [],
  onOpenRearrange: null,
};

export default MobileElementActionBar;
